import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { PersonLinkService } from '../../services/personLinkService';
import { Person } from '../../types/person';

interface ClaimProfileButtonProps {
  person: Person;
  onSuccess?: () => void;
}

/**
 * Button for users to claim their person profile
 * Shows only if:
 * - User is authenticated
 * - User doesn't have a personId already
 * - No pending request exists
 */
const ClaimProfileButton: React.FC<ClaimProfileButtonProps> = ({
  person,
  onSuccess,
}) => {
  const { authState } = useAuth();
  const user = authState.user;

  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user already has pending request or approved link
  useEffect(() => {
    const checkPendingRequest = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const pending = await PersonLinkService.getUserPendingRequest(user.uid);
        setHasPendingRequest(!!pending);
      } catch (err) {
        console.error('Error checking pending request:', err);
      } finally {
        setLoading(false);
      }
    };

    checkPendingRequest();
  }, [user?.uid, user?.personId]);

  // Don't show if:
  // - User not authenticated
  // - User already linked to ANY person (not just this one)
  if (!user || user.personId) {
    return null;
  }

  // Show loading while checking for pending request
  if (loading) {
    return null;
  }

  // Show "pending" message if request exists
  if (hasPendingRequest) {
    return (
      <Alert severity="info" icon={<LinkIcon />}>
        You have a pending profile link request awaiting admin approval.
      </Alert>
    );
  }

  const handleSubmit = async () => {
    if (!user) return;

    setSubmitting(true);
    setError(null);

    try {
      await PersonLinkService.createLinkRequest({
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName,
        personId: person.id,
        personName: `${person.firstName} ${person.lastName}`,
      });

      setSuccess(true);

      // Close dialog after brief delay
      setTimeout(() => {
        setShowDialog(false);
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting link request:', err);
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<LinkIcon />}
        onClick={() => setShowDialog(true)}
      >
        This is me
      </Button>

      <Dialog
        open={showDialog}
        onClose={() => !submitting && setShowDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Claim Your Profile
        </DialogTitle>

        <DialogContent>
          {success ? (
            <Alert severity="success">
              Request submitted successfully! An administrator will review your request.
            </Alert>
          ) : (
            <>
              <Typography paragraph>
                You are about to request to link your user account to{' '}
                <strong>{person.firstName} {person.lastName}</strong>'s family tree profile.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                This request requires admin approval. Once approved, you'll be able to:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>Edit your own family tree profile (changes require approval)</li>
                  <li>Your user profile will show a link to your family tree profile</li>
                </Box>
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setShowDialog(false)}
            disabled={submitting}
          >
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              Submit Request
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClaimProfileButton;
